import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ShieldCheck, Clock, WifiOff, CheckCircle, House } from 'lucide-react'
import { Button } from '@ascleo/ui/components/ui/button'
import { useAccessibilitySession, type ResetReason } from '../accessibility-session'
import { Body } from '../../../components/typography/body'
import { Subtitle } from '../../../components/typography/subtitle'
import { Title } from '../../../components/typography/title'
import { useSizing } from '../../../lib/use-sizing'

/** Long enough for an elderly patient to read the reassurance (KO-18). */
const OVERLAY_AUTO_DISMISS_SECONDS = 20

/**
 * Icon and messaging for each reset reason.
 */
function useResetContent(reason: ResetReason | null) {
  const { t } = useTranslation()

  switch (reason) {
    case 'timeout':
      return {
        icon: Clock,
        iconTile: 'bg-warning/25 text-warning-ink',
        title: t('self-service.reset-overlay.timeout-title'),
        subtitle: t('self-service.reset-overlay.timeout-subtitle'),
      }
    case 'offline':
      return {
        icon: WifiOff,
        iconTile: 'bg-destructive-subtle text-destructive-ink',
        title: t('self-service.reset-overlay.offline-title'),
        subtitle: t('self-service.reset-overlay.offline-subtitle'),
      }
    case 'interruption':
      return {
        icon: ShieldCheck,
        iconTile: 'bg-primary-subtle text-primary-ink',
        title: t('self-service.reset-overlay.interruption-title'),
        subtitle: t('self-service.reset-overlay.interruption-subtitle'),
      }
    case 'completion':
    default:
      return {
        icon: CheckCircle,
        iconTile: 'bg-success-subtle text-success-ink',
        title: t('self-service.reset-overlay.completion-title'),
        subtitle: t('self-service.reset-overlay.completion-subtitle'),
      }
  }
}

/**
 * Shared reset/reassurance overlay.
 *
 * Displays a full-screen overlay when a session ends through timeout,
 * interruption, offline disconnection, or normal completion. Communicates
 * that the session has ended safely and auto-dismisses after a short period.
 *
 * @since 1.0.0
 */
export function ResetReassuranceOverlay() {
  const { showResetOverlay, resetReason, completeReset } = useAccessibilitySession()
  const { t } = useTranslation()
  const sizing = useSizing()
  const [autoDismissCountdown, setAutoDismissCountdown] = useState(OVERLAY_AUTO_DISMISS_SECONDS)
  const content = useResetContent(resetReason)

  useEffect(function syncAutoDismissTimers() {
    if (!showResetOverlay) return

    setAutoDismissCountdown(OVERLAY_AUTO_DISMISS_SECONDS)

    const interval = setInterval(() => {
      setAutoDismissCountdown((prev) => {
        if (prev <= 1) return 0
        return prev - 1
      })
    }, 1000)
    const dismissTimeout = setTimeout(completeReset, OVERLAY_AUTO_DISMISS_SECONDS * 1000)

    return function clearAutoDismissTimers() {
      clearInterval(interval)
      clearTimeout(dismissTimeout)
    }
  }, [showResetOverlay, completeReset])

  if (!showResetOverlay) return null

  const Icon = content.icon

  return (
    <div
      data-testid="frontdesk-reset-reassurance-overlay"
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background/95 backdrop-blur-md"
      role="alert"
      aria-live="assertive"
    >
      <div className={`mx-auto flex w-full max-w-xl flex-col items-center ${sizing.containerGap} p-8 text-center`}>
        {/* One single-tone icon per reason (KO-97) */}
        <span
          aria-hidden
          className={`grid size-24 place-items-center rounded-3xl ${content.iconTile}`}
        >
          <Icon
            data-testid="frontdesk-reset-reassurance-icon"
            className="size-12"
          />
        </span>

        {/* Main message */}
        <Title size={sizing.title} className="font-heading font-normal text-balance">
          {content.title}
        </Title>

        <Subtitle size={sizing.subtitle} className="text-center text-muted-foreground">
          {content.subtitle}
        </Subtitle>

        {/* Reassurance message */}
        <Body
          data-testid="frontdesk-reset-reassurance-message"
          size={sizing.body}
          intent="muted"
          className="mx-auto max-w-[44ch] text-center"
        >
          {t('self-service.reset-overlay.reassurance')}
        </Body>

        {/* Auto-dismiss indicator */}
        <Body
          data-testid="frontdesk-reset-reassurance-countdown"
          size={sizing.body}
          intent="muted"
          className="text-center"
        >
          {t('self-service.reset-overlay.auto-dismiss', { seconds: autoDismissCountdown })}
        </Body>

        {/* Manual dismiss: a full-size primary action with an icon (KO-97, FB-13) */}
        <Button
          data-testid="frontdesk-reset-reassurance-dismiss-btn"
          type="button"
          size="2xl"
          onClick={completeReset}
          className="mt-2"
        >
          <House aria-hidden className="size-6" />
          {t('self-service.reset-overlay.dismiss')}
        </Button>
      </div>
    </div>
  )
}
